import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import {
  acceptRequest,
  declineRequest,
  findByFriendCode,
  isOnline,
  removeFriend,
  sendFriendRequest,
  sendInvite,
} from "../lib/friends";
import { PUBLIC_GAMES } from "../lib/room";
import { levelInfo } from "../lib/xp";
import { Avatar, copyText } from "./ui";
import { useI18n, useT } from "../i18n";
import Pic from "./pics";

export function useFriends(uid) {
  const [ids, setIds] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    if (!uid) return;
    const offFriends = onSnapshot(collection(db, "users", uid, "friends"), (s) => setIds(s.docs.map((d) => d.id)));
    const offReq = onSnapshot(collection(db, "users", uid, "requests"), (s) =>
      setRequests(s.docs.map((d) => ({ uid: d.id, ...d.data() })))
    );
    return () => {
      offFriends();
      offReq();
    };
  }, [uid]);

  const idsKey = ids.join(",");
  useEffect(() => {
    if (!idsKey) {
      setProfiles({});
      return;
    }
    const list = idsKey.split(",");
    const offs = list.map((id) =>
      onSnapshot(doc(db, "users", id), (s) => setProfiles((p) => ({ ...p, [id]: s.exists() ? { uid: id, ...s.data() } : null })))
    );
    setProfiles((p) => Object.fromEntries(Object.entries(p).filter(([k]) => list.includes(k))));
    return () => offs.forEach((off) => off());
  }, [idsKey]);

  const friends = ids
    .map((id) => profiles[id])
    .filter(Boolean)
    .sort((a, b) => Number(isOnline(b)) - Number(isOnline(a)) || a.displayName.localeCompare(b.displayName));

  return { friends, requests };
}

// Who you're already friends with, who asked you, and who you've asked this
// visit, for "Add friend" buttons next to players in a room.
export function useFriendLinks() {
  const t = useT();
  const { profile } = useAuth();
  const { toast } = useToast();
  const uid = profile?.uid;
  const [friendIds, setFriendIds] = useState([]);
  const [askedMe, setAskedMe] = useState([]);
  const [sent, setSent] = useState({});

  useEffect(() => {
    if (!uid) return;
    const offFriends = onSnapshot(collection(db, "users", uid, "friends"), (s) => setFriendIds(s.docs.map((d) => d.id)));
    const offReq = onSnapshot(collection(db, "users", uid, "requests"), (s) => setAskedMe(s.docs.map((d) => d.id)));
    return () => {
      offFriends();
      offReq();
    };
  }, [uid]);

  // "me" | "guest" | "friends" | "asked" (they sent you a request) | "sent" | "none",
  // or null when you're not signed in.
  const status = (p) => {
    if (!profile) return null;
    if (p.id === uid) return "me";
    if (p.isGuest) return "guest";
    if (friendIds.includes(p.id)) return "friends";
    if (askedMe.includes(p.id)) return "asked";
    return sent[p.id] ? "sent" : "none";
  };

  const add = async (p) => {
    try {
      const res = await sendFriendRequest(profile, p.id);
      setSent((m) => ({ ...m, [p.id]: true }));
      toast({
        emoji: res === "already" ? "smile" : res === "accepted" ? "friends" : "mail",
        title:
          res === "sent"
            ? t("Friend request sent to {name}", { name: p.name })
            : res === "accepted"
            ? t("You and {name} are now friends!", { name: p.name })
            : t("Already friends with {name}", { name: p.name }),
      });
    } catch (e) {
      toast({ emoji: "warning", kind: "error", title: t("Couldn't add friend"), body: e.message });
    }
  };

  return { status, add, signedIn: !!profile };
}

// The button for one player: Add friend, Accept, or how things stand.
export function FriendButton({ player, links, compact }) {
  const t = useT();
  const st = links.status(player);
  if (st === "none" || st === "asked")
    return (
      <button
        className={compact ? "icon-btn friend-add" : "btn btn-sm btn-primary"}
        title={st === "asked" ? t("Accept {name}'s friend request", { name: player.name }) : t("Add {name} as a friend", { name: player.name })}
        aria-label={st === "asked" ? t("Accept {name}'s friend request", { name: player.name }) : t("Add {name} as a friend", { name: player.name })}
        onClick={() => links.add(player)}
      >
        {compact ? <Pic name="plus" /> : st === "asked" ? t("Accept") : t("Add friend")}
      </button>
    );
  if (compact) return st === "friends" ? <span className="friend-tag" title={t("Friends")}>
        <Pic name="friends" />
      </span> : null;
  if (st === "friends") return <span className="friend-state">{t("Friends")}</span>;
  if (st === "sent") return <span className="friend-state">{t("Request sent")}</span>;
  return null;
}

function FriendRow({ friend, me, roomCode, game, invited, onInvite }) {
  const t = useT();
  const navigate = useNavigate();
  const { toast } = useToast();
  const online = isOnline(friend);
  const st = friend.status;
  const [confirmRemove, setConfirmRemove] = useState(false);
  // Casino tables stay hidden: friends just see "a game".
  const gameName = PUBLIC_GAMES[st?.game] ? t(PUBLIC_GAMES[st.game].name) : t("a game");

  return (
    <li className="friend-row">
      <div className="friend-avatar">
        <Avatar avatar={friend.avatar} size={40} level={levelInfo(friend.xp).level} />
        <span className={`presence-dot ${online ? "on" : ""}`} />
      </div>
      <div className="friend-info">
        <strong>{friend.displayName}</strong>
        <small>
          {!online
            ? t("Offline")
            : st?.roomCode
            ? (st.playing ? t("Playing {game}", { game: gameName }) : t("In the lobby of {game}", { game: gameName }))
            : t("Online")}
        </small>
      </div>
      <div className="friend-actions">
        {roomCode ? (
          <button className="btn btn-sm btn-primary" disabled={invited} onClick={() => onInvite(friend)}>
            {invited ? t("Sent ✓") : t("Invite")}
          </button>
        ) : (
          online &&
          st?.roomCode && (
            <button className="btn btn-sm btn-primary" onClick={() => navigate(`/r/${st.roomCode}`)}>
              {t("Join")}
            </button>
          )
        )}
        {!roomCode &&
          (confirmRemove ? (
            <>
              <button
                className="btn btn-sm btn-danger"
                onClick={() =>
                  removeFriend(me, friend.uid).then(() => toast({ emoji: "hand", title: t("Removed {name}", { name: friend.displayName }) }))
                }
              >
                {t("Remove")}
              </button>
              <button className="btn btn-sm btn-ghost" onClick={() => setConfirmRemove(false)}>
                {t("Keep")}
              </button>
            </>
          ) : (
            <button className="icon-btn" title={t("Remove friend")} aria-label={t("Remove friend")} onClick={() => setConfirmRemove(true)}>
              ⋯
            </button>
          ))}
      </div>
    </li>
  );
}

export default function FriendsPanel({ roomCode, game }) {
  const { t, tx } = useI18n();
  const { profile, isGuest, user, openAuth } = useAuth();
  const { toast } = useToast();
  const { friends, requests } = useFriends(profile?.uid);
  const [tab, setTab] = useState("friends");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [invited, setInvited] = useState({});

  if (!profile) {
    return (
      <div className="panel friends-panel friends-locked">
        <h3>{t("Friends")}</h3>
        <p className="muted">
          {user && isGuest
            ? t("Guests can play, but friends & invites need an account.")
            : t("Sign in to get a friend code and build your squad.")}
        </p>
        <button className="btn btn-primary btn-block" onClick={() => openAuth("signup")}>
          {t("Create free account")}
        </button>
      </div>
    );
  }

  const addFriend = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const target = await findByFriendCode(code);
      if (!target) {
        toast({ emoji: "search", title: t("No player with that code"), body: t("Double-check the 6 characters.") });
        return;
      }
      const res = await sendFriendRequest(profile, target.uid);
      const name = target.displayName;
      const note = {
        sent: { emoji: "mail", title: t("Request sent to {name}", { name }) },
        accepted: { emoji: "friends", title: t("You and {name} are now friends!", { name }) },
        already: { emoji: "smile", title: t("You're already friends with {name}", { name }) },
      }[res];
      toast(note);
      setCode("");
    } catch (err) {
      toast({ emoji: "warning", kind: "error", title: t("Couldn't send request"), body: err.message });
    } finally {
      setBusy(false);
    }
  };

  const invite = async (friend) => {
    try {
      await sendInvite(profile, friend.uid, roomCode, game);
      setInvited((i) => ({ ...i, [friend.uid]: true }));
      toast({ emoji: "invite", title: t("Invite sent to {name}", { name: friend.displayName }) });
    } catch (e) {
      toast({ emoji: "warning", kind: "error", title: t("Invite failed"), body: e.message });
    }
  };

  const onlineCount = friends.filter(isOnline).length;

  return (
    <div className="panel friends-panel">
      <div className="panel-head">
        <h3>{t("Friends")}</h3>
        <button
          className="friend-code small"
          title={t("Your friend code - share it!")}
          onClick={async () => (await copyText(profile.friendCode)) && toast({ emoji: "copy", title: t("Friend code copied!") })}
        >
          #{profile.friendCode} <Pic name="copy" />
        </button>
      </div>

      {!roomCode && (
        <div className="tabs">
          <button className={tab === "friends" ? "active" : ""} onClick={() => setTab("friends")}>
            {t("Friends")} <span className="pill">{onlineCount}/{friends.length}</span>
          </button>
          <button className={tab === "requests" ? "active" : ""} onClick={() => setTab("requests")}>
            {t("Requests")} {requests.length > 0 && <span className="pill pill-hot">{requests.length}</span>}
          </button>
        </div>
      )}

      {!roomCode && (
        <form className="add-friend" onSubmit={addFriend}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={t("Friend code e.g. {code}", { code: "K7QM2X" })}
            maxLength={8}
          />
          <button className="btn btn-primary" disabled={busy || code.replace(/[^A-Z0-9]/gi, "").length < 6}>
            {t("Add")}
          </button>
        </form>
      )}

      {tab === "requests" && !roomCode ? (
        <ul className="friend-list">
          {requests.length === 0 && <li className="empty">{t("No pending requests.")}</li>}
          {requests.map((r) => (
            <li key={r.uid} className="friend-row">
              <Avatar avatar={r.fromAvatar} size={40} />
              <div className="friend-info">
                <strong>{r.fromName}</strong>
                <small>#{r.fromCode}</small>
              </div>
              <div className="friend-actions">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() =>
                    acceptRequest(profile.uid, r.uid).then(() =>
                      toast({ emoji: "friends", title: t("You and {name} are now friends!", { name: r.fromName }) })
                    )
                  }
                >
                  {t("Accept")}
                </button>
                <button className="btn btn-sm btn-ghost" onClick={() => declineRequest(profile.uid, r.uid)}>
                  {t("Decline")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="friend-list">
          {friends.length === 0 && (
            <li className="empty">{tx("No friends yet - share your code <b>#{code}</b> with your friends!", { code: profile.friendCode })}</li>
          )}
          {(roomCode ? friends.filter(isOnline) : friends).map((f) => (
            <FriendRow
              key={f.uid}
              friend={f}
              me={profile.uid}
              roomCode={roomCode}
              game={game}
              invited={invited[f.uid]}
              onInvite={invite}
            />
          ))}
          {roomCode && friends.length > 0 && onlineCount === 0 && <li className="empty">{t("None of your friends are online.")}</li>}
        </ul>
      )}
    </div>
  );
}
